import React, { useMemo } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import Loading from '../Loading/Loading';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { GROUPS } from '../../pages/CostBreakup/costBreakupTableColumns';

interface ColumnDefinition {
  key: string;
  label: string;
}

interface MultilevelHeaderTableProps {
  loading: boolean;
  columns: ColumnDefinition[];
  sortedEntries: [string, any][];
  onSort: (key: string) => void;
  currency?: string;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  summableColumns?: string[];
}

export const MultilevelHeaderTable: React.FC<MultilevelHeaderTableProps> = ({
  loading = false,
  columns = [],
  sortedEntries,
  onSort,
  currency = 'USD',
  sortColumn,
  sortDirection,
  summableColumns = [],
}) => {
  // Format currency values
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const totals = useMemo(() => {
    const result: Record<string, number> = {};

    if (!sortedEntries) return result;

    // Ensure all summable columns are at least initialized to 0
    summableColumns?.forEach((key) => {
      result[key] = 0;
    });

    sortedEntries.forEach(([_, row]) => {
      columns.forEach((col) => {
        if (summableColumns?.includes(col.key)) {
          const raw = row[col.key];
          const val =
            typeof raw === 'number' ? Number(raw.toFixed(2)) : Number(raw) || 0;
          result[col.key] += val;
        }
      });
    });

    return result;
  }, [sortedEntries, columns, summableColumns]);

  const availableKeys = new Set(columns.map((col) => col.key));
  const completeGroups = new Set(
    Object.entries(GROUPS)
      .filter(([_, keys]) => keys.every((k) => availableKeys.has(k)))
      .map(([g]) => g)
  );

  // Build GROUP map
  const groupMap: Record<string, string> = {};
  for (const [groupName, keys] of Object.entries(GROUPS)) {
    if (!completeGroups.has(groupName)) continue;
    for (const k of keys) {
      if (!(k in groupMap)) {
        groupMap[k] = groupName; // first complete group wins
      }
    }
  }

  // Compute header rows
  const topLevelHeaders: {
    label: string;
    colSpan: number;
    rowSpan: number;
    keys: string[];
  }[] = [];
  const subHeaders: ColumnDefinition[] = [];

  columns.forEach((col) => {
    const group = groupMap[col.key];
    if (group) {
      // Add to group
      let existing = topLevelHeaders.find((h) => h.label === group);
      if (!existing) {
        existing = { label: group, colSpan: 0, rowSpan: 1, keys: [] };
        topLevelHeaders.push(existing);
      }
      existing.keys.push(col.key);
      existing.colSpan += 1;
      subHeaders.push(col);
    } else {
      // Not part of any group
      topLevelHeaders.push({
        label: col.label,
        colSpan: 1,
        rowSpan: 2,
        keys: [col.key],
      });
    }
  });

  // Get styles for trend indicators
  const getTrendStyles = (color?: string) => {
    const baseStyle: React.CSSProperties = {
      fontWeight: 400,
      position: 'relative',
      ...(color && color !== 'black' && { paddingRight: '20px' }),
    };

    // Background color for specific trends
    const backgroundColors = {
      green: 'rgba(92, 184, 92, 0.1)',
      red: 'rgba(217, 83, 79, 0.1)',
      orange: 'rgba(250, 144, 0, 0.08)',
    };

    // Text color mapping
    const textColors = {
      green: 'rgba(92, 184, 92, 1)',
      red: 'rgba(217, 83, 79, 1)',
      orange: 'rgba(250, 144, 0, 1)',
      black: 'black',
    };

    // Trend indicator styles
    const trendIndicatorStyle: React.CSSProperties = {
      position: 'absolute',
      top: '57%',
      transform: 'translateY(-50%)',
    };

    // Determine trend based on color
    const getTrend = (color?: string) => {
      switch (color) {
        case 'green':
          return 'up';
        case 'red':
        case 'orange':
          return 'down';
        case 'black':
          return 'neutral';
        default:
          return 'neutral';
      }
    };

    const trend = getTrend(color);

    const trendIndicators = {
      up: <ArrowDownwardIcon />,
      down: <ArrowUpwardIcon />,
      neutral: '',
    };

    return {
      style: {
        ...baseStyle,
        color: color ? textColors[color as keyof typeof textColors] : 'black',
        backgroundColor:
          trend === 'up' && color === 'green'
            ? backgroundColors.green
            : trend === 'down' && color === 'red'
            ? backgroundColors.red
            : trend === 'down' && color === 'orange'
            ? backgroundColors.orange
            : 'transparent',
      },
      trendIndicator:
        trend !== 'neutral'
          ? {
              ...trendIndicatorStyle,
              color:
                trend === 'up'
                  ? 'rgba(92, 184, 92, 1)'
                  : trend === 'down' && color === 'red'
                  ? 'rgba(217, 83, 79, 1)'
                  : 'rgba(250, 144, 0, 1)',
            }
          : {},
      trendSymbol: trendIndicators[trend],
    };
  };

  // Render sort indicator
  const renderSortIndicator = (column: string) => {
    if (!sortColumn || sortColumn !== column) return null;

    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  if (loading) {
    return (
      <Paper className="table-container">
        <Loading style={{ padding: '2rem' }} />
      </Paper>
    );
  }

  if (!sortedEntries || sortedEntries.length === 0) {
    return (
      <Paper className="table-container">
        <div style={{ padding: '2rem', textAlign: 'center' }}>No Records</div>
      </Paper>
    );
  }

  return (
    <Paper className="table-container">
      <Table
        className="table adminTable adminPanelTable"
        size="small"
        stickyHeader
        aria-label="reusable cost table"
      >
        <TableHead className="admintableHead">
          {/* First row: Group headers and non-grouped columns */}
          <TableRow>
            {topLevelHeaders.map((header) => (
              <TableCell
                key={header.label}
                align="center"
                colSpan={header.colSpan}
                rowSpan={header.rowSpan}
                onClick={
                  header.rowSpan === 2
                    ? () => onSort(header.keys[0])
                    : undefined
                }
                className="adminTableheading"
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 8,
                  cursor: header.rowSpan === 2 ? 'pointer' : 'default',
                  textAlign: 'center',
                }}
              >
                {header.label}
                {header.rowSpan === 2 && renderSortIndicator(header.keys[0])}
              </TableCell>
            ))}
          </TableRow>

          {/* Second row: Sub-headers for grouped columns */}
          {topLevelHeaders.some((h) => h.rowSpan === 1) && (
            <TableRow>
              {topLevelHeaders.flatMap((header) =>
                header.rowSpan === 1
                  ? header.keys.map((key) => {
                      const col = columns.find((c) => c.key === key);
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          onClick={() => col && onSort(col.key)}
                          className="adminTableheading"
                          style={{
                            position: 'sticky',
                            top: 40,
                            zIndex: 8,
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          {col?.label}
                          {col && renderSortIndicator(col.key)}
                        </TableCell>
                      );
                    })
                  : []
              )}
            </TableRow>
          )}
        </TableHead>

        <TableBody>
          {sortedEntries.map(([rowKey, rowData]) => (
            <TableRow key={rowKey}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  style={{ textAlign: 'center' }}
                >
                  {['cost', 'price', 'charges'].some((keyword) =>
                    col.key.toLowerCase().includes(keyword)
                  )
                    ? formatCurrency(rowData?.[col.key])
                    : rowData?.[col.key] === '' ||
                      rowData?.[col.key] == null ||
                      rowData?.[col.key] === undefined
                    ? '-'
                    : !isNaN(rowData?.[col.key]) &&
                      typeof rowData?.[col.key] !== 'string'
                    ? Number(rowData?.[col.key]).toFixed(2)
                    : rowData?.[col.key]?.toString() ?? '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {/* Footer total row */}
          {summableColumns.length > 0 && (
            <TableRow>
              {columns.map((col, index) => {
                // First column is always "Total"
                if (index === 0) {
                  return (
                    <TableCell
                      key={col.key}
                      className="adminTableheading"
                      align="center"
                      style={{
                        position: 'sticky',
                        bottom: 0,
                        background: '#f5f5f5',
                        zIndex: 8,
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      Total
                    </TableCell>
                  );
                }

                // For summable columns, show the total
                if (summableColumns.includes(col.key)) {
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      className="adminTableheading"
                      style={{
                        position: 'sticky',
                        bottom: 0,
                        background: '#f5f5f5',
                        color: 'rgba(10, 60, 162, 1)',
                        zIndex: 8,
                        textAlign: 'center',
                      }}
                    >
                      {['cost', 'price', 'charges'].some((keyword) =>
                          col.key.toLowerCase().includes(keyword)
                        )
                        ? formatCurrency(totals[col.key])
                        : Number(totals[col.key])
                            .toFixed(2)
                            ?.toLocaleString() ?? ''}
                    </TableCell>
                  );
                }

                // For non-summable columns, show empty cell
                return (
                  <TableCell
                    key={col.key}
                    align="center"
                    className="adminTableheading"
                    style={{
                      position: 'sticky',
                      bottom: 0,
                      background: '#f5f5f5',
                      zIndex: 8,
                      textAlign: 'center',
                    }}
                  >
                    {/* Empty cell for non-summable column */}
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
